// Polls (M60, docs/chat-round-three.md, D220). The question and options are
// sealed like text; votes are metadata the server keeps and broadcasts as
// message:poll. These are the pure parts: what a vote does to the counts
// (shown at once, rolled back if the server says no), when results show,
// and the close times the composer offers.
import { POLL_LIMITS, type PollState } from '@twinden/shared';
import type { ChatMessage } from '../components/chat/messageUtils';

/** Your choices, from the votes everyone can see. */
export function myVotes(poll: PollState, userId: string | undefined): number[] {
  if (!userId) return [];
  return poll.votes
    .filter((vote) => vote.userId === userId)
    .map((vote) => vote.optionIndex)
    .sort((a, b) => a - b);
}

/**
 * The poll after you vote for `choices` (replacing what you had, D220), or
 * take your vote back with null: what the bubble shows before the server
 * answers.
 */
export function withMyVote(
  poll: PollState,
  userId: string,
  choices: number[] | null
): PollState {
  const votes = [
    ...poll.votes.filter((vote) => vote.userId !== userId),
    ...(choices ?? []).map((optionIndex) => ({ userId, optionIndex })),
  ];
  const counts = Array.from({ length: poll.options }, () => 0);
  for (const vote of votes) {
    if (vote.optionIndex < counts.length) counts[vote.optionIndex]! += 1;
  }
  return {
    ...poll,
    votes,
    counts,
    myVotes: [...(choices ?? [])].sort((a, b) => a - b),
  };
}

/** Results show once you've voted or the poll is closed (D220). */
export function showsResults(
  poll: PollState,
  userId: string | undefined
): boolean {
  return poll.closed || myVotes(poll, userId).length > 0;
}

/** Everyone who voted, once each (for "4 votes" on a multiple-choice poll). */
export function voterCount(poll: PollState): number {
  return new Set(poll.votes.map((vote) => vote.userId)).size;
}

/** A message:poll event applied to a chat's messages. */
export function applyPollEvent(
  list: ChatMessage[],
  event: { messageId: string; poll: PollState }
): ChatMessage[] {
  return list.map((message) =>
    message.id === event.messageId ? { ...message, poll: event.poll } : message
  );
}

export type CloseAfter = 'never' | 'hour' | 'day' | 'week';
const CLOSE_AFTER_SECONDS: Record<CloseAfter, number | null> = {
  never: null,
  hour: 60 * 60,
  day: 24 * 60 * 60,
  week: POLL_LIMITS.MAX_OPEN_MS / 1000,
};

/**
 * How long after it arrives a poll closes, or null for never. Relative, so
 * a poll that waited in the outbox still gets its full time: the server
 * sets the close time on arrival (D220).
 */
export function closesInSeconds(choice: CloseAfter): number | null {
  return CLOSE_AFTER_SECONDS[choice];
}

/** A draft poll's options, trimmed, with empty rows dropped. */
export function cleanOptions(options: string[]): string[] {
  return options.map((option) => option.trim()).filter(Boolean);
}

/**
 * A vote as the bubble sees it (D220): shown at once, then the server's
 * answer. If the server refuses or can't be reached, my previous votes go
 * back onto whatever the poll shows by then, so other people's votes that
 * arrived meanwhile (message:poll) stay. Votes aren't queued offline.
 * Resolves true when the server took it.
 */
export async function castVote(input: {
  before: PollState;
  userId: string;
  choices: number[] | null;
  /** Updates the poll from what's on screen now. */
  setPoll: (update: (current: PollState) => PollState) => void;
  send: () => Promise<PollState>;
}): Promise<boolean> {
  const previous = myVotes(input.before, input.userId);
  input.setPoll((current) => withMyVote(current, input.userId, input.choices));
  try {
    const answer = await input.send();
    input.setPoll(() => answer);
    return true;
  } catch {
    input.setPoll((current) =>
      withMyVote(current, input.userId, previous.length > 0 ? previous : null)
    );
    return false;
  }
}
